// Admin Settings → System → Inventory rollout.
//
// Reads the four inventory knobs out of admin config and writes them back. The
// deployed environment is the floor underneath: a knob with no row reads as
// whatever the server was started with, which is why the panel shows the
// resolved answer rather than "unset".
//
// Every edit is a draft until it is reviewed. Applying is production-affecting
// in one click, so the confirm step names which direction it moves.

import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { useT } from '../../i18n'
import {
  ROLLOUT_MODES,
  inventoryRolloutI18n,
  type RolloutMode,
} from '../../i18n/settings/inventory-rollout'
import { fetchOrgDirectory } from '../../api/org-directory'
import { apiUrl } from '../../lib/api'

const ROLLOUT_QUERY_KEY = ['admin', 'config', 'inventory-rollout'] as const
const READINESS_QUERY_KEY = ['admin', 'inventory', 'lifecycle-readiness'] as const

const LIFECYCLE_KEY = 'inventory.lifecycle_enabled'
const MODE_KEY = 'inventory.order_rollout_mode'
const ORG_IDS_KEY = 'inventory.lifecycle_org_ids'
const PO_BRIDGE_KEY = 'inventory.po_bridge_enabled'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export type Rollout = {
  lifecycleEnabled: boolean
  mode: RolloutMode
  orgIds: string[]
  poBridgeEnabled: boolean
}

type Readiness = {
  rollout_mode: string
  live_escrows: number
  stale_escrows: number
  negative_inventory_rows: number
  duplicate_committed_lines: number
  duplicate_receive_sources: number
  // N-35: finalized purchase attempts whose label was voided locally while the
  // carrier never confirmed; each blocks its order until confirmed by hand.
  void_unconfirmed_finalized?: number
  void_unconfirmed_orders?: string[]
  oldest_recovery_age_seconds: number | null
  ready: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The listing projects the whitelist over current rows, so a key with no row
 * comes back with a null value. That is not an error — it means the deployed
 * environment applies. The panel cannot see the environment, so it falls back
 * to the safe reading (off) rather than inventing a state.
 */
function parseRollout(payload: unknown): Rollout {
  const items = isRecord(payload) && Array.isArray(payload.items) ? payload.items : []
  const byKey = new Map<string, unknown>()
  for (const item of items) {
    // The listing nests the row under `value`, which is null for a whitelisted
    // key that has no row yet — the shape is one level deeper than the
    // single-key GET, which returns the row itself.
    if (isRecord(item) && typeof item.config_key === 'string') {
      const row = item.value
      byKey.set(item.config_key, isRecord(row) ? row.config_value : undefined)
    }
  }

  const scalar = (key: string): unknown => {
    const value = byKey.get(key)
    return isRecord(value) ? value.value : undefined
  }

  const rawMode = scalar(MODE_KEY)
  const rawOrgIds = byKey.get(ORG_IDS_KEY)

  return {
    lifecycleEnabled: scalar(LIFECYCLE_KEY) === true,
    mode: ROLLOUT_MODES.includes(rawMode as RolloutMode) ? (rawMode as RolloutMode) : 'off',
    orgIds: Array.isArray(rawOrgIds) ? rawOrgIds.map(String).filter((id) => UUID_RE.test(id)) : [],
    poBridgeEnabled: scalar(PO_BRIDGE_KEY) === true,
  }
}

async function readRollout(): Promise<Rollout> {
  const res = await fetch(apiUrl('/admin/config'))
  if (!res.ok) throw new Error(`admin config ${res.status}`)
  return parseRollout(await res.json())
}

async function readReadiness(): Promise<Readiness> {
  const res = await fetch(apiUrl('/admin/inventory/lifecycle-readiness'))
  if (!res.ok) throw new Error(`readiness ${res.status}`)
  return (await res.json()) as Readiness
}

async function putKey(key: string, value: unknown, valueType: string): Promise<void> {
  const res = await fetch(apiUrl(`/admin/config/${key}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ config_value: value, value_type: valueType }),
  })
  if (!res.ok) throw new Error(`put ${key} ${res.status}`)
}

/**
 * Written one key at a time because that is the surface the config API offers.
 * The master switch goes LAST when enabling and FIRST when disabling, so a
 * failure part-way through can only ever leave the lifecycle narrower than
 * asked — never wider.
 */
async function writeRollout(next: Rollout): Promise<void> {
  const body = async () => {
    await putKey(MODE_KEY, { value: next.mode }, 'string')
    await putKey(ORG_IDS_KEY, next.orgIds, 'array')
    await putKey(PO_BRIDGE_KEY, { value: next.poBridgeEnabled }, 'boolean')
  }

  if (next.lifecycleEnabled) {
    await body()
    await putKey(LIFECYCLE_KEY, { value: true }, 'boolean')
  } else {
    await putKey(LIFECYCLE_KEY, { value: false }, 'boolean')
    await body()
  }
}

function sameRollout(a: Rollout, b: Rollout): boolean {
  return (
    a.lifecycleEnabled === b.lifecycleEnabled &&
    a.mode === b.mode &&
    a.poBridgeEnabled === b.poBridgeEnabled &&
    a.orgIds.length === b.orgIds.length &&
    a.orgIds.every((id, i) => id === b.orgIds[i])
  )
}

function HelpHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
      {children}
    </h3>
  )
}

function Row({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'warn'
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-sm text-[var(--color-text-muted)]">{label}</span>
      <span
        className={
          'text-sm font-semibold tabular-nums ' +
          (tone === 'warn' ? 'text-[var(--color-danger,#b91c1c)]' : 'text-[var(--color-text)]')
        }
      >
        {value}
      </span>
    </div>
  )
}

export function InventoryRolloutControl() {
  const t = useT(inventoryRolloutI18n)
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Rollout | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [saved, setSaved] = useState(false)
  const [orgInput, setOrgInput] = useState('')
  const [orgError, setOrgError] = useState<string | null>(null)

  const rolloutQuery = useQuery<Rollout>({
    queryKey: ROLLOUT_QUERY_KEY,
    queryFn: readRollout,
    retry: false,
  })
  const readinessQuery = useQuery<Readiness>({
    queryKey: READINESS_QUERY_KEY,
    queryFn: readReadiness,
    retry: false,
  })
  // Names are a convenience: an internal organization may not appear on the
  // buy-side directory at all, so a listed id without a name is normal.
  const directoryQuery = useQuery({
    queryKey: ['org-directory', 'seller'],
    queryFn: () => fetchOrgDirectory('seller'),
    retry: false,
  })

  const current = rolloutQuery.data ?? null
  const shown = draft ?? current
  const dirty = current !== null && draft !== null && !sameRollout(draft, current)

  const orgNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const org of directoryQuery.data ?? []) map.set(org.orgId, org.orgName)
    return map
  }, [directoryQuery.data])

  const mutation = useMutation({
    mutationFn: writeRollout,
    onSuccess: () => {
      setDraft(null)
      setReviewOpen(false)
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: ROLLOUT_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: READINESS_QUERY_KEY })
    },
  })

  function edit(patch: Partial<Rollout>) {
    if (!shown) return
    setSaved(false)
    setDraft({ ...shown, ...patch })
  }

  function addOrg() {
    if (!shown) return
    const id = orgInput.trim()
    if (!UUID_RE.test(id)) {
      setOrgError(t.cohort.invalid)
      return
    }
    if (shown.orgIds.includes(id)) {
      setOrgError(t.cohort.duplicate)
      return
    }
    setOrgError(null)
    setOrgInput('')
    edit({ orgIds: [...shown.orgIds, id] })
  }

  if (rolloutQuery.isLoading) {
    return (
      <section className="max-w-3xl" aria-labelledby="inventory-rollout-title">
        <h2 id="inventory-rollout-title" className="text-lg font-semibold text-[var(--color-text)]">
          {t.title}
        </h2>
        <p role="status" className="mt-3 text-sm text-[var(--color-text-muted)]">
          {t.loading}
        </p>
      </section>
    )
  }

  if (!shown) {
    return (
      <section className="max-w-3xl" aria-labelledby="inventory-rollout-title">
        <h2 id="inventory-rollout-title" className="text-lg font-semibold text-[var(--color-text)]">
          {t.title}
        </h2>
        <p className="mt-3 text-sm text-[var(--color-danger,#b91c1c)]">{t.loadError}</p>
      </section>
    )
  }

  const effectiveMode = shown.lifecycleEnabled ? shown.mode : 'off'
  const statusLine =
    effectiveMode === 'off'
      ? t.status.off
      : effectiveMode === 'all'
        ? t.status.all
        : t.status.allowlist(shown.orgIds.length)
  const readiness = readinessQuery.data

  return (
    <section className="max-w-3xl space-y-5" aria-labelledby="inventory-rollout-title">
      <header>
        <h2 id="inventory-rollout-title" className="text-lg font-semibold text-[var(--color-text)]">
          {t.title}
        </h2>
        <p className="mt-1 text-sm leading-5 text-[var(--color-text-muted)]">{t.description}</p>
      </header>

      <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.status.heading}
        </h3>
        <p className="mt-2 text-base font-semibold text-[var(--color-text)]">{statusLine}</p>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {shown.lifecycleEnabled && shown.poBridgeEnabled
            ? t.status.poBridgeOn
            : t.status.poBridgeOff}
        </p>
      </div>

      {readiness && (
        <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
              {t.readiness.heading}
            </h3>
            <Badge variant={readiness.ready ? 'success' : 'warning'}>
              {readiness.ready ? t.readiness.ready : t.readiness.notReady}
            </Badge>
          </div>
          <div className="mt-2 divide-y divide-[var(--color-border)]">
            <Row label={t.readiness.liveEscrows} value={String(readiness.live_escrows)} />
            <Row
              label={t.readiness.staleEscrows}
              value={String(readiness.stale_escrows)}
              tone={readiness.stale_escrows > 0 ? 'warn' : undefined}
            />
            <Row
              label={t.readiness.negativeRows}
              value={String(readiness.negative_inventory_rows)}
              tone={readiness.negative_inventory_rows > 0 ? 'warn' : undefined}
            />
            <Row
              label={t.readiness.duplicateLines}
              value={String(readiness.duplicate_committed_lines)}
              tone={readiness.duplicate_committed_lines > 0 ? 'warn' : undefined}
            />
            <Row
              label={t.readiness.duplicateReceipts}
              value={String(readiness.duplicate_receive_sources)}
              tone={readiness.duplicate_receive_sources > 0 ? 'warn' : undefined}
            />
            <Row
              label={t.readiness.voidUnconfirmed}
              value={String(readiness.void_unconfirmed_finalized ?? 0)}
              tone={(readiness.void_unconfirmed_finalized ?? 0) > 0 ? 'warn' : undefined}
            />
            {(readiness.void_unconfirmed_orders?.length ?? 0) > 0 && (
              <div className="py-1.5">
                <p className="text-xs text-[var(--color-text-muted)]">
                  {t.readiness.voidUnconfirmedHint}
                </p>
                <ul className="mt-1 flex flex-wrap gap-2">
                  {readiness.void_unconfirmed_orders?.map((orderId) => (
                    <li key={orderId}>
                      <Link
                        to="/admin/orders/$id"
                        params={{ id: orderId }}
                        data-testid={`void-unconfirmed-order-${orderId}`}
                        className="font-mono text-xs font-semibold text-[var(--color-text)] hover:underline"
                      >
                        {orderId.slice(0, 8)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Row
              label={t.readiness.oldestRecovery}
              value={
                readiness.oldest_recovery_age_seconds === null
                  ? t.readiness.none
                  : t.readiness.seconds(readiness.oldest_recovery_age_seconds)
              }
              tone={
                readiness.oldest_recovery_age_seconds !== null &&
                readiness.oldest_recovery_age_seconds > 900
                  ? 'warn'
                  : undefined
              }
            />
          </div>
        </div>
      )}

      <fieldset className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.master.heading}
        </legend>
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={shown.lifecycleEnabled}
            onChange={(e) => edit({ lifecycleEnabled: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-action)]"
          />
          <span>
            <span className="block text-sm font-semibold text-[var(--color-text)]">
              {t.master.label}
            </span>
            <span className="mt-0.5 block text-sm text-[var(--color-text-muted)]">
              {t.master.hint}
            </span>
          </span>
        </label>
      </fieldset>

      <fieldset className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.mode.heading}
        </legend>
        <p className="mb-3 text-sm text-[var(--color-text-muted)]">{t.mode.hint}</p>
        <div className="space-y-2">
          {ROLLOUT_MODES.map((mode) => (
            <label key={mode} className="flex cursor-pointer items-start gap-3">
              <input
                type="radio"
                name="inventory-rollout-mode"
                value={mode}
                checked={shown.mode === mode}
                onChange={() => edit({ mode })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-action)]"
              />
              <span>
                <span className="block text-sm font-semibold text-[var(--color-text)]">
                  {mode === 'off' ? t.mode.off : mode === 'allowlist' ? t.mode.allowlist : t.mode.all}
                </span>
                <span className="mt-0.5 block text-sm text-[var(--color-text-muted)]">
                  {mode === 'off'
                    ? t.mode.offHint
                    : mode === 'allowlist'
                      ? t.mode.allowlistHint
                      : t.mode.allHint}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.cohort.heading}
        </h3>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.cohort.hint}</p>

        {shown.orgIds.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t.cohort.empty}</p>
        ) : (
          <ul className="mt-3 divide-y divide-[var(--color-border)]">
            {shown.orgIds.map((id) => (
              <li key={id} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-[var(--color-text)]">
                    {orgNames.get(id) ?? t.cohort.unknownOrg}
                  </span>
                  <code className="block truncate font-mono text-[10px] text-[var(--color-text-muted)]">
                    {id}
                  </code>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => edit({ orgIds: shown.orgIds.filter((o) => o !== id) })}
                >
                  {t.cohort.remove}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex items-start gap-2">
          <span className="flex-1">
            <input
              value={orgInput}
              onChange={(e) => {
                setOrgInput(e.target.value)
                setOrgError(null)
              }}
              placeholder={t.cohort.addPlaceholder}
              aria-label={t.cohort.addPlaceholder}
              aria-invalid={orgError !== null}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-xs text-[var(--color-text)]"
            />
            {orgError && (
              <span role="alert" className="mt-1 block text-xs text-[var(--color-danger,#b91c1c)]">
                {orgError}
              </span>
            )}
          </span>
          <Button size="sm" onClick={addOrg}>
            {t.cohort.add}
          </Button>
        </div>
      </div>

      <fieldset className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.poBridge.heading}
        </legend>
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={shown.poBridgeEnabled}
            onChange={(e) => edit({ poBridgeEnabled: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-action)]"
          />
          <span>
            <span className="block text-sm font-semibold text-[var(--color-text)]">
              {t.poBridge.label}
            </span>
            <span className="mt-0.5 block text-sm text-[var(--color-text-muted)]">
              {t.poBridge.hint}
            </span>
          </span>
        </label>
      </fieldset>

      <div className="flex items-center gap-3">
        <Button onClick={() => setReviewOpen(true)} disabled={!dirty || mutation.isPending}>
          {t.review}
        </Button>
        {dirty && (
          <>
            <Badge variant="warning">{t.pending}</Badge>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
              {t.discard}
            </Button>
          </>
        )}
        {saved && !dirty && (
          <span role="status" className="text-sm text-[var(--color-text-muted)]">
            {t.saved}
          </span>
        )}
        {mutation.isError && (
          <span role="alert" className="text-sm text-[var(--color-danger,#b91c1c)]">
            {t.saveError}
          </span>
        )}
      </div>

      <div className="border-t border-[var(--color-border)] pt-3">
        <Button variant="link" size="sm" className="px-0" onClick={() => setHelpOpen(true)}>
          {t.help.link}
        </Button>
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.help.title}</DialogTitle>
            <DialogDescription>{t.help.intro}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 text-sm text-[var(--color-text-muted)]">
            <section className="space-y-2">
              <HelpHeading>{t.help.statesHeading}</HelpHeading>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] p-3">
                  <p className="text-sm font-semibold text-[var(--color-text)]">
                    {t.help.offTitle}
                  </p>
                  <p className="mt-1 leading-5">{t.help.offBody}</p>
                </div>
                <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] p-3">
                  <p className="text-sm font-semibold text-[var(--color-text)]">
                    {t.help.onTitle}
                  </p>
                  <p className="mt-1 leading-5">{t.help.onBody}</p>
                </div>
              </div>
            </section>

            <section className="space-y-2">
              <HelpHeading>{t.help.readingHeading}</HelpHeading>
              <p className="leading-5">{t.help.readingBody}</p>
              <ul className="space-y-1.5 leading-5">
                {[t.help.live, t.help.stale, t.help.negative, t.help.duplicates, t.help.recovery].map(
                  (line) => (
                    <li key={line} className="flex gap-2">
                      <span aria-hidden="true" className="text-[var(--color-text-muted)]">
                        &middot;
                      </span>
                      <span>{line}</span>
                    </li>
                  ),
                )}
              </ul>
            </section>

            <section className="space-y-2">
              <HelpHeading>{t.help.turningHeading}</HelpHeading>
              <p className="leading-5">{t.help.turningOn}</p>
              <p className="leading-5">{t.help.turningOff}</p>
            </section>

            <section className="space-y-2">
              <HelpHeading>{t.help.trapsHeading}</HelpHeading>
              <ul className="space-y-1.5 leading-5">
                {[t.help.trapTtl, t.help.trapPoBridge, t.help.trapNarrow].map((line) => (
                  <li key={line} className="flex gap-2">
                    <span aria-hidden="true" className="text-[var(--color-text-muted)]">
                      &middot;
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setHelpOpen(false)}>
              {t.help.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.reviewTitle}</DialogTitle>
            <DialogDescription>{t.reviewBody}</DialogDescription>
          </DialogHeader>
          {current && draft && current.lifecycleEnabled !== draft.lifecycleEnabled && (
            <p className="text-sm font-semibold text-[var(--color-text)]">
              {draft.lifecycleEnabled ? t.reviewEnable : t.reviewDisable}
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewOpen(false)}>
              {t.cancel}
            </Button>
            <Button
              onClick={() => draft && mutation.mutate(draft)}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t.saving : t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
